import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { HotelProvider } from './context/HotelContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ToastContainer, { installGlobalToast } from './components/Toast';
import SoldierBackground from './components/SoldierBackground';
import SolapurDealsWidget from './components/SolapurDealsWidget';
import { SettingsProvider } from './context/SettingsContext';

installGlobalToast();

// Lazy load page components to optimize bundle size and load times
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Menu = lazy(() => import('./pages/Menu'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const Admin = lazy(() => import('./pages/Admin'));
const Administrator = lazy(() => import('./pages/Administrator'));
const CustomerOrders = lazy(() => import('./pages/CustomerOrders'));
const KitchenDashboard = lazy(() => import('./pages/KitchenDashboard'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const Payments = lazy(() => import('./pages/Payments'));
const Analytics = lazy(() => import('./pages/Analytics'));
const TableSession = lazy(() => import('./pages/TableSession'));
const ParcelSession = lazy(() => import('./pages/ParcelSession'));
const ParcelMenu = lazy(() => import('./pages/ParcelMenu'));
const ParcelCheckout = lazy(() => import('./pages/ParcelCheckout'));
const SubscriptionExpired = lazy(() => import('./pages/SubscriptionExpired'));
const SuperAdminLogin = lazy(() => import('./pages/SuperAdminLogin'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));

function PageLoader() {
  return (
    <div className='min-h-[50vh] flex flex-col items-center justify-center'>
      <div className='w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin'></div>
      <p className='mt-4 text-xs text-gray-400 font-semibold tracking-wider uppercase animate-pulse'>Loading Page...</p>
    </div>
  );
}

/**
 * HotelRoutes — renders inside /hotel/:hotelSlug/*
 * The HotelProvider wraps all these so useHotel() works everywhere.
 */
function HotelRoutes() {
  return (
    <HotelProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path='/' element={<Home />} />
          <Route path='/about' element={<About />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/menu' element={<Menu />} />
          <Route path='/login' element={<Login />} />
          <Route path='/register' element={<Register />} />
          <Route path='/cart' element={<Cart />} />
          <Route path='/checkout' element={<Checkout />} />
          <Route path='/confirmation' element={<Confirmation />} />
          <Route path='/admin' element={<Admin />} />
          <Route path='/my-orders' element={<CustomerOrders />} />
          <Route path='/kitchen' element={<KitchenDashboard />} />
          <Route path='/manager' element={<ManagerDashboard />} />
          <Route path='/payments' element={<Payments />} />
          <Route path='/analytics' element={<Analytics />} />
          <Route path='/table/:token' element={<TableSession />} />
          <Route path='/session/:token' element={<TableSession />} />
          <Route path='/table-session' element={<TableSession />} />
          {/* Parcel Ordering Flow */}
          <Route path='/parcel' element={<ParcelSession />} />
          <Route path='/parcel/menu' element={<ParcelMenu />} />
          <Route path='/parcel/checkout' element={<ParcelCheckout />} />
          {/* Subscription expired */}
          <Route path='/subscription-expired' element={<SubscriptionExpired />} />
          {/* Hidden admin route */}
          <Route path='/administrator' element={<Administrator />} />
        </Routes>
      </Suspense>
    </HotelProvider>
  );
}

/**
 * Redirects legacy root URLs to the active multi-tenant path.
 * Checks localStorage for last active hotel slug, defaulting to 'shreyas'.
 */
function LegacyRedirect({ to }) {
  const lastSlug = localStorage.getItem('lastHotelSlug') || 'shreyas';
  return <Navigate to={`/hotel/${lastSlug}${to}`} replace />;
}

export default function App() {
  const location = useLocation();
  const isSuperAdminRoute = location.pathname.startsWith('/sys-panel-7x9k');

  return (
    <SocketProvider>
      <SettingsProvider>
        <NotificationProvider>
          <div className='min-h-screen flex flex-col bg-transparent relative isolate'>
            <SoldierBackground />
            <SolapurDealsWidget />
            <ToastContainer />
            {!isSuperAdminRoute && <Navbar />}
            <main className={isSuperAdminRoute ? 'relative z-10 flex-1 w-full' : 'relative z-10 flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* ── Multi-tenant hotel routes ── */}
                  <Route path='/hotel/:hotelSlug/*' element={<HotelRoutes />} />

                  {/* ── Legacy root routes (backward compatible) ── */}
                  <Route path='/' element={<LegacyRedirect to='' />} />
                  <Route path='/about' element={<LegacyRedirect to='/about' />} />
                  <Route path='/contact' element={<LegacyRedirect to='/contact' />} />
                  <Route path='/menu' element={<LegacyRedirect to='/menu' />} />
                  <Route path='/login' element={<LegacyRedirect to='/login' />} />
                  <Route path='/register' element={<LegacyRedirect to='/register' />} />
                  <Route path='/cart' element={<LegacyRedirect to='/cart' />} />
                  <Route path='/checkout' element={<LegacyRedirect to='/checkout' />} />
                  <Route path='/confirmation' element={<LegacyRedirect to='/confirmation' />} />
                  <Route path='/admin' element={<LegacyRedirect to='/admin' />} />
                  <Route path='/my-orders' element={<LegacyRedirect to='/my-orders' />} />
                  <Route path='/kitchen' element={<LegacyRedirect to='/kitchen' />} />
                  <Route path='/manager' element={<LegacyRedirect to='/manager' />} />
                  <Route path='/payments' element={<LegacyRedirect to='/payments' />} />
                  <Route path='/analytics' element={<LegacyRedirect to='/analytics' />} />
                  <Route path='/parcel' element={<LegacyRedirect to='/parcel' />} />
                  <Route path='/parcel/menu' element={<LegacyRedirect to='/parcel/menu' />} />
                  <Route path='/parcel/checkout' element={<LegacyRedirect to='/parcel/checkout' />} />
                  <Route path='/subscription-expired' element={<SubscriptionExpired />} />
                  <Route path='/administrator' element={<Administrator />} />

                  {/* ── Super Admin (secret URL, no hotel context) ── */}
                  <Route path='/sys-panel-7x9k/login' element={<SuperAdminLogin />} />
                  <Route path='/sys-panel-7x9k' element={<SuperAdminPanel />} />
                </Routes>
              </Suspense>
            </main>
            {!isSuperAdminRoute && <Footer />}
          </div>
        </NotificationProvider>
      </SettingsProvider>
    </SocketProvider>
  );
}
